/**
 * formatters — Access-parity date / number formatters cho Production Report
 * (skill `/table-style` §9).
 *
 * - Date    : `DD-MMM-YYYY`     (vd `12-May-2026`)
 * - Datetime: `DD-MMM-YYYY HH:mm`
 * - Number  : `1,234.56` (2 decimals if non-integer) hoặc `1,234` (integer)
 * - Percent : `12.35%` (input là decimal 0..1)
 *
 * Khác:
 * - `fmtMt`       : weight 3 decimals (legacy spec Batch Report yêu cầu).
 * - `fmtUsd`      : `$ 1,234` integer; `fmtUsdPrecise` 2 decimals.
 * - `fmtVarPct`  : variance percent `+1.23% / -0.45%` cho variance cells.
 */
const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function formatAccessDate(value: unknown): string {
  if (value == null || value === '') return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

export function formatAccessDateTime(value: unknown): string {
  if (value == null || value === '') return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

/** Bọc thân số trong ngoặc kế toán khi âm: body='1,234.50' → '(1,234.50)'. */
const acct = (neg: boolean, body: string): string => (neg ? `(${body})` : body)

export function formatAccessNumber(value: number, decimals?: number): string {
  const abs = Math.abs(value)
  let body: string
  if (decimals != null) body = abs.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  else if (Number.isInteger(value)) body = abs.toLocaleString('en-US')
  else body = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return acct(value < 0, body)
}

export function fmtInt(v: unknown): string {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  return acct(n < 0, Math.round(Math.abs(n)).toLocaleString('en-US'))
}

export function fmtMt(v: unknown): string {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  return acct(n < 0, Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }))
}

export function fmtPercent(v: unknown, decimals = 2): string {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  return acct(n < 0, Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }))
}

export function fmtPercentScaled(v: unknown, decimals = 2): string {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  return acct(n < 0, (Math.abs(n) * 100).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + '%')
}

/** Variance percent kế toán: dương → '1.23%'; âm → '(0.45%)' (số âm trong ngoặc,
 *  chị Đẹp 2026-06-22 — bỏ ký hiệu +/− cũ). */
export function fmtVarPct(v: unknown, decimals = 2): string {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  return acct(n < 0, (Math.abs(n) * 100).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + '%')
}

export function fmtCurrency(v: unknown): string {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  return acct(n < 0, Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
}

export function fmtUsd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  return acct(n < 0, '$ ' + Math.round(Math.abs(n)).toLocaleString('en-US'))
}

export function fmtUsdPrecise(v: unknown): string {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  return acct(n < 0, '$ ' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
}

export function fmtPlain(v: unknown, decimals = 2): string {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  return acct(n < 0, Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }))
}
